const bannerTitle = 'Coffee, cookies, your call';
const bannerDescription = 'We use essential cookies to keep the shop running. With your permission, optional cookies help us and our partners improve and personalise your experience, understand site use, measure advertising and show relevant ads. Change your choice anytime.';
const bannerTitleSelector = '#shopify-pc__banner__body-title';
const nativeBannerSelector = '#shopify-pc__banner';
const legacyBannerSelector = '.shopify-section--privacy-banner';
const acceptLabel = 'Accept all';
const declineLabel = 'Reject all';
const manageLabel = 'Manage';
const preferencesSelector = '#shopify-pc__prefs';
const preferencesDialogSelector = '#shopify-pc__prefs__dialog';
const preferencesOverlaySelector = '#shopify-pc__prefs__overlay';
const preferencesCloseSelector = '#shopify-pc__prefs__header-close';
const preferencesActionSelector = [
  preferencesCloseSelector,
  '#shopify-pc__prefs__header-accept',
  '#shopify-pc__prefs__header-decline',
  '#shopify-pc__prefs__header-save',
].join(', ');
const preferencesAcceptLabel = 'Accept all';
const preferencesDeclineLabel = 'Essential only';
const preferencesSaveLabel = 'Save preferences';
const bannerExitDuration = 200;
const preferencesTransitionDuration = 280;
const preferencesTransitionEasing = 'cubic-bezier(0.23, 1, 0.32, 1)';
const bannerActionSelector = [
  '#shopify-pc__banner__btn-accept',
  '#shopify-pc__banner__btn-decline',
  '#shopify-pc__banner__btn-manage-prefs',
].join(', ');
let replayingAction = null;
let pendingPreferencesOpen = null;
let activePreferencesAnimations = [];
const exitingBanners = new WeakSet();

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isPreferencesSurfaceVisible = (preferences) => {
  if (!preferences || preferences.style?.display === 'none') {
    return false;
  }

  if (typeof window.getComputedStyle !== 'function') {
    return true;
  }

  const styles = window.getComputedStyle(preferences);
  return styles.display !== 'none' && styles.visibility !== 'hidden';
};

const getPreferencesSurface = (node = null) => {
  const scopedSurface = node?.closest?.(preferencesSelector);
  if (scopedSurface) {
    return scopedSurface;
  }

  const surfaces = [...document.querySelectorAll(preferencesSelector)];
  return surfaces.find(isPreferencesSurfaceVisible) || surfaces[0] || null;
};

const getPreferencesElement = (preferences, selector) =>
  preferences?.querySelector?.(selector) ||
  (preferences ? null : document.querySelector(selector));

const pruneHiddenPreferencesSurfaces = (activePreferences = null) => {
  document.querySelectorAll(preferencesSelector).forEach((preferences) => {
    if (preferences === activePreferences || isPreferencesSurfaceVisible(preferences)) {
      return;
    }

    preferences.remove?.();
  });
};

const getPreferencesBaseTransform = (dialog) => {
  if (typeof window.getComputedStyle !== 'function') {
    return 'none';
  }

  return (
    window
      .getComputedStyle(dialog)
      .getPropertyValue('--sigma-cookie-surface-transform')
      .trim() || 'none'
  );
};

const composePreferencesTransform = (baseTransform, translateY, scale) => {
  const base = baseTransform === 'none' ? '' : `${baseTransform} `;
  return `${base}translate3d(0, ${translateY}px, 0) scale(${scale})`.trim();
};

const getPreferencesTransitionOffset = () => {
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 640px)').matches
  ) {
    return 16;
  }

  return 10;
};

const cancelPreferencesAnimations = () => {
  activePreferencesAnimations.forEach((animation) => animation.cancel());
  activePreferencesAnimations = [];
};

const clearPreferencesTransitionStyles = (dialog, overlay, banner) => {
  dialog?.style.removeProperty('transform');
  dialog?.style.removeProperty('opacity');
  dialog?.style.removeProperty('filter');
  overlay?.style.removeProperty('opacity');
  banner?.style.removeProperty('transform');
  banner?.style.removeProperty('opacity');
  banner?.style.removeProperty('filter');
};

const playPreferencesTransition = (dialog, overlay, banner, closing = false) => {
  const baseTransform = getPreferencesBaseTransform(dialog);
  const offset = getPreferencesTransitionOffset();
  const dialogTransitionTransform = composePreferencesTransform(baseTransform, offset, 0.985);
  const animations = [];

  if (prefersReducedMotion() || typeof dialog.animate !== 'function') {
    clearPreferencesTransitionStyles(dialog, overlay, banner);
    return Promise.resolve();
  }

  try {
    animations.push(
      dialog.animate(
        closing
          ? [
              { opacity: 1, transform: baseTransform, filter: 'blur(0)' },
              { opacity: 0, transform: dialogTransitionTransform, filter: 'blur(3px)' },
            ]
          : [
              { opacity: 0, transform: dialogTransitionTransform, filter: 'blur(3px)' },
              { opacity: 1, transform: baseTransform, filter: 'blur(0)' },
            ],
        {
          duration: preferencesTransitionDuration,
          easing: preferencesTransitionEasing,
          fill: 'both',
        },
      ),
    );

    if (banner?.animate) {
      animations.push(
        banner.animate(
          closing
            ? [
                {
                  opacity: 0,
                  transform: getBannerTransform(banner, offset),
                  filter: 'blur(3px)',
                },
                { opacity: 1, transform: getBannerTransform(banner, 0), filter: 'blur(0)' },
              ]
            : [
                { opacity: 1, transform: getBannerTransform(banner, 0), filter: 'blur(0)' },
                {
                  opacity: 0,
                  transform: getBannerTransform(banner, offset),
                  filter: 'blur(3px)',
                },
              ],
          {
            duration: preferencesTransitionDuration,
            easing: preferencesTransitionEasing,
            fill: 'both',
          },
        ),
      );
    }

    if (overlay?.animate) {
      animations.push(
        overlay.animate(
          closing
            ? [{ opacity: 1 }, { opacity: 0 }]
            : [{ opacity: 0 }, { opacity: 1 }],
          {
            duration: preferencesTransitionDuration,
            easing: preferencesTransitionEasing,
            fill: 'both',
          },
        ),
      );
    }
  } catch {
    clearPreferencesTransitionStyles(dialog, overlay, banner);
    return Promise.resolve();
  }

  activePreferencesAnimations = animations;

  return Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).finally(() => {
    if (activePreferencesAnimations !== animations) {
      return;
    }

    animations.forEach((animation) => animation.cancel());
    activePreferencesAnimations = [];
    clearPreferencesTransitionStyles(dialog, overlay, banner);
  });
};

const setBannerSettingsState = (banner, open) => {
  if (!banner) {
    return;
  }

  if (open) {
    banner.dataset.sigmaCookieState = 'settings';
    banner.setAttribute('aria-hidden', 'true');
    banner.setAttribute('inert', '');
    if ('inert' in banner) {
      banner.inert = true;
    }
    return;
  }

  delete banner.dataset.sigmaCookieState;
  banner.removeAttribute('aria-hidden');
  banner.removeAttribute('inert');
  if ('inert' in banner) {
    banner.inert = false;
  }
};

const setBannerTransitionState = (banner, state) => {
  if (!banner) {
    return;
  }

  banner.dataset.sigmaCookieState = state;
  banner.setAttribute('aria-hidden', 'true');
  banner.setAttribute('inert', '');
  if ('inert' in banner) {
    banner.inert = true;
  }
};

const getBannerPositionTransform = (banner) => {
  if (typeof window.getComputedStyle !== 'function') {
    return 'translateX(0)';
  }

  return (
    window
      .getComputedStyle(banner)
      .getPropertyValue('--sigma-cookie-banner-position-transform')
      .trim() || 'translateX(0)'
  );
};

const getBannerTransform = (banner, offset) =>
  `${getBannerPositionTransform(banner)} translateY(${offset}px)`;

const releaseBannerExit = (banner, animation) => {
  let observer;
  let recoveryTimeout;

  const cancelAnimation = () => {
    observer?.disconnect();
    window.clearTimeout(recoveryTimeout);
    animation?.cancel();
    exitingBanners.delete(banner);
  };

  if (banner.hidden || banner.style.display === 'none') {
    cancelAnimation();
    return;
  }

  observer = new MutationObserver(() => {
    if (banner.hidden || banner.style.display === 'none') {
      cancelAnimation();
    }
  });

  observer.observe(banner, {
    attributes: true,
    attributeFilter: ['hidden', 'style'],
  });

  recoveryTimeout = window.setTimeout(cancelAnimation, 10000);
};

const playBannerExit = (banner, action) => {
  exitingBanners.add(banner);
  let animation;
  let isComplete = false;

  const completeAction = () => {
    if (isComplete) {
      return;
    }

    isComplete = true;
    replayingAction = action;

    try {
      action.click();
    } finally {
      releaseBannerExit(banner, animation);
      replayingAction = null;
    }
  };

  try {
    animation = banner.animate(
      [
        {
          opacity: 1,
          transform: getBannerTransform(banner, 0),
          filter: 'blur(0)',
        },
        {
          opacity: 0,
          transform: getBannerTransform(banner, 14),
          filter: 'blur(3px)',
        },
      ],
      {
        duration: bannerExitDuration,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
        fill: 'forwards',
      },
    );

    animation.finished.then(completeAction).catch(completeAction);
  } catch {
    completeAction();
  }
};

const syncPreferences = () => {
  const preferences = getPreferencesSurface();
  pruneHiddenPreferencesSurfaces(preferences);
  const dialog = getPreferencesElement(preferences, preferencesDialogSelector);
  const actions = getPreferencesElement(preferences, '.shopify-pc__prefs__header-actions');
  const accept = getPreferencesElement(preferences, '#shopify-pc__prefs__header-accept');
  const decline = getPreferencesElement(preferences, '#shopify-pc__prefs__header-decline');
  const save = getPreferencesElement(preferences, '#shopify-pc__prefs__header-save');

  if (!dialog || !actions || !accept || !decline || !save) {
    return false;
  }

  accept.textContent = preferencesAcceptLabel;
  decline.textContent = preferencesDeclineLabel;
  save.textContent = preferencesSaveLabel;
  save.classList.add('primary');
  actions.append(decline, accept, save);
  dialog.setAttribute('data-sigma-cookie-ready', 'true');

  if (pendingPreferencesOpen) {
    if (isPreferencesSurfaceVisible(preferences)) {
      const { banner } = pendingPreferencesOpen;
      pendingPreferencesOpen = null;
      const overlay = getPreferencesElement(preferences, preferencesOverlaySelector);

      setBannerTransitionState(banner, 'opening');
      preferences.dataset.sigmaCookieState = 'opening';
      overlay?.style.setProperty('opacity', '0');

      playPreferencesTransition(dialog, overlay, banner).then(() => {
        if (preferences.dataset.sigmaCookieState !== 'opening') {
          return;
        }

        setBannerSettingsState(banner, true);
        preferences.dataset.sigmaCookieState = 'open';
        getPreferencesElement(preferences, preferencesCloseSelector)?.focus({ preventScroll: true });
      });
    }
  }

  return true;
};

const cleanupPreferencesState = (focusManage = false, preferencesOverride = null) => {
  cancelPreferencesAnimations();

  const banner = document.querySelector(nativeBannerSelector);
  const preferences = preferencesOverride || getPreferencesSurface();
  const dialog = getPreferencesElement(preferences, preferencesDialogSelector);
  const overlay = getPreferencesElement(preferences, preferencesOverlaySelector);

  setBannerSettingsState(banner, false);
  preferences?.removeAttribute('data-sigma-cookie-state');
  dialog?.style.removeProperty('transform');
  dialog?.style.removeProperty('opacity');
  dialog?.style.removeProperty('filter');
  overlay?.style.removeProperty('opacity');
  banner?.style.removeProperty('transform');
  banner?.style.removeProperty('opacity');
  banner?.style.removeProperty('filter');

  if (focusManage) {
    document.querySelector('#shopify-pc__banner__btn-manage-prefs')?.focus({ preventScroll: true });
  }

  pruneHiddenPreferencesSurfaces(preferences);
};

const replayPreferencesAction = (action) => {
  replayingAction = action;
  try {
    action.click();
  } finally {
    replayingAction = null;
  }
};

const playPreferencesExit = (action, closeOnly = false) => {
  const preferences = getPreferencesSurface(action);
  const dialog = getPreferencesElement(preferences, preferencesDialogSelector);
  const overlay = getPreferencesElement(preferences, preferencesOverlaySelector);
  const banner = document.querySelector(nativeBannerSelector);

  if (
    !preferences ||
    !dialog ||
    !banner ||
    !isPreferencesSurfaceVisible(preferences) ||
    preferences.dataset.sigmaCookieState === 'closing'
  ) {
    replayPreferencesAction(action);
    if (closeOnly) {
      cleanupPreferencesState(true, preferences);
    }
    return;
  }

  cancelPreferencesAnimations();
  setBannerTransitionState(banner, 'closing');
  preferences.dataset.sigmaCookieState = 'closing';

  playPreferencesTransition(dialog, overlay, banner, true).then(() => {
    replayPreferencesAction(action);
    cleanupPreferencesState(closeOnly, preferences);
  });
};

const openPreferences = (banner, action) => {
  pendingPreferencesOpen = {
    banner,
  };

  // The native manage button keeps this banner instance. showPreferences()
  // rebuilds and hides it, leaving nothing to return to when settings close.
  replayPreferencesAction(action);
  syncPreferences();

  if (pendingPreferencesOpen && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(syncPreferences);
  }
};

const handlePreferencesAction = (event) => {
  const target = event.target;
  const action = target?.closest?.(preferencesActionSelector);
  const preferences = getPreferencesSurface(action);
  const overlay = getPreferencesElement(preferences, preferencesOverlaySelector);
  const isOverlay = target === overlay;

  if ((!action && !isOverlay) || (action && action === replayingAction)) {
    return;
  }

  if (!isPreferencesSurfaceVisible(preferences)) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  const closeAction = getPreferencesElement(preferences, preferencesCloseSelector);
  const selectedAction = isOverlay ? closeAction : action;
  if (!selectedAction) {
    return;
  }

  playPreferencesExit(selectedAction, selectedAction === closeAction);
};

const handlePreferencesKeydown = (event) => {
  if (event.key !== 'Escape') {
    return;
  }

  const preferences = getPreferencesSurface();
  const closeAction = getPreferencesElement(preferences, preferencesCloseSelector);
  if (
    !isPreferencesSurfaceVisible(preferences) ||
    !closeAction ||
    closeAction === replayingAction
  ) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  playPreferencesExit(closeAction, true);
};

const handleBannerAction = (event) => {
  const action = event.target.closest(bannerActionSelector);
  const banner = action?.closest(nativeBannerSelector);

  if (!action || !banner || action === replayingAction) {
    return;
  }

  if (exitingBanners.has(banner)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (action.id === 'shopify-pc__banner__btn-manage-prefs') {
    event.preventDefault();
    event.stopImmediatePropagation();
    openPreferences(banner, action);
    return;
  }

  if (prefersReducedMotion()) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  playBannerExit(banner, action);
};

const hideLegacyBanner = () => {
  document.querySelectorAll(legacyBannerSelector).forEach((banner) => {
    banner.hidden = true;
    banner.setAttribute('aria-hidden', 'true');
  });

  return true;
};

const updateBanner = () => {
  const banner = document.querySelector(nativeBannerSelector);
  const title = document.querySelector(bannerTitleSelector);
  const actions = document.querySelector('.shopify-pc__banner__btns');
  const accept = document.querySelector('#shopify-pc__banner__btn-accept');
  const decline = document.querySelector('#shopify-pc__banner__btn-decline');
  const manage = document.querySelector('#shopify-pc__banner__btn-manage-prefs');

  if (!title || !actions || !accept || !decline || !manage) {
    return false;
  }

  title.textContent = bannerTitle;
  const policyLink = banner.querySelector('#shopify-pc__banner__body-policy-link');
  const description = policyLink?.previousSibling;
  if (description?.nodeType === Node.TEXT_NODE) {
    const policySentence = description.textContent.lastIndexOf('Learn more in our');
    if (policySentence !== -1) {
      // Retain the native privacy sentence and link, including its attributes and styling.
      description.textContent = `${bannerDescription} ${description.textContent.slice(policySentence)}`;
    }
  }
  accept.classList.add('button');
  decline.classList.add('button');
  manage.classList.add('button');
  accept.textContent = acceptLabel;
  decline.textContent = declineLabel;

  const manageText = manage.querySelector('span');
  if (manageText) {
    manageText.textContent = manageLabel;
  } else {
    manage.textContent = manageLabel;
  }

  actions.append(accept, decline, manage);
  if (typeof banner?.setAttribute === 'function') {
    banner.setAttribute('data-sigma-cookie-ready', 'true');
  }

  if (actions.dataset.sigmaCookieMotion !== 'true') {
    actions.dataset.sigmaCookieMotion = 'true';
    actions.addEventListener('click', handleBannerAction, { capture: true });
  }

  return true;
};

const syncBanners = () => {
  const banner = document.querySelector(nativeBannerSelector);
  hideLegacyBanner();
  const preferencesReady = syncPreferences();

  if (!banner || banner.dataset?.sigmaCookieReady === 'true') {
    return Boolean(banner) || preferencesReady;
  }

  return updateBanner();
};

let previewBannerInitialized = false;

const initializePreviewBanner = () => {
  // Shopify's boot method skips both banner startup and its preferences-link
  // listener when pb=0. loadBanner retains Shopify's region and consent checks.
  if (
    previewBannerInitialized ||
    new URL(window.location.href).searchParams.get('pb') !== '0' ||
    typeof window.privacyBanner?.loadBanner !== 'function'
  ) {
    return;
  }

  previewBannerInitialized = true;
  try {
    const result = window.privacyBanner.loadBanner();
    result?.catch?.(() => { previewBannerInitialized = false; });
  } catch {
    previewBannerInitialized = false;
  }
};

const handlePersistentPreferences = (event) => {
  const action = event.target?.closest?.('[data-cookie-settings]');
  if (!action || typeof window.privacyBanner?.showPreferences !== 'function') return;
  if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  event.preventDefault();
  // Shopify also handles the documented link fragment. Avoid opening twice.
  event.stopImmediatePropagation();
  const banner = document.querySelector(nativeBannerSelector);
  const manage = banner?.querySelector('#shopify-pc__banner__btn-manage-prefs');
  if (manage && isPreferencesSurfaceVisible(banner)) {
    openPreferences(banner, manage);
    return;
  }
  try {
    const result = window.privacyBanner.showPreferences();
    result?.catch?.(() => window.location.assign(action.href));
  } catch {
    window.location.assign(action.href);
  }
};

const observer = new MutationObserver(syncBanners);

observer.observe(document.body, {
  childList: true,
});

if (typeof document.addEventListener === 'function') {
  document.addEventListener('click', handlePersistentPreferences, { capture: true });
  document.addEventListener('click', handlePreferencesAction, { capture: true });
  document.addEventListener('keydown', handlePreferencesKeydown, { capture: true });
  document.addEventListener('load', initializePreviewBanner, { capture: true });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', initializePreviewBanner);
}

syncBanners();
if (window.location?.href) initializePreviewBanner();
